import React, { useState } from 'react'
import { Argument as ClassName } from 'classnames'

import { isUserHasAccessToConnector } from '../is-user-has-access-to-connector'
import { OcppAuraConnectorControl } from '../ocpp-aura-connector-control'
import { RemoteControlWidget } from '../remote-control-widget'
import { RemoteOcppDimmerControl } from '../remote-ocpp-dimmer-control'
import { SelectRFIDTagModal } from '../../common/components/select-rfid-tag-modal'
import {
    ChargePoint,
    useChargePointOverrideScheduleForOngoingChargingSessionMutation,
    useChargePointRemoteStartMutation,
    useChargePointRemoteStopMutation,
    useGreenChargingChargePointStatusQuery,
} from '../../common/redux/api/web-api'
import { remoteStopIsAvailable } from '../get-connector-state/get-connector-state'
import { isAdminDomain } from '../currentDomain'
import { showErrorMessagesForIgnoreSchedule } from '../../common/utils/chargePointUtils'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'

export type ChargePointOcppAuraControlWidgetProps = {
    isReadOnly: boolean
    userId?: string
    chargePoint: ChargePoint
    className?: ClassName
    style?: React.CSSProperties
}

export function ChargePointOcppAuraControlWidget({
    userId,
    isReadOnly = false,
    chargePoint,
}: ChargePointOcppAuraControlWidgetProps): React.ReactElement {
    const isOnline = chargePoint.chargePointStatus !== 'Offline'
    const [remoteStart] = useChargePointRemoteStartMutation()
    const [remoteStop] = useChargePointRemoteStopMutation()
    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: chargePoint.id })
    const [selectRfidModalIsOpen, setSelectRfidModalIsOpen] = useState(false)
    const [currentConnector, setCurrentConnector] = useState(1)
    const [overrideSchedule] = useChargePointOverrideScheduleForOngoingChargingSessionMutation()
    const toast = useToast()

    const text = useText()

    const handleRemoteStart = async (connectorId: number, rfidTag?: string) => {
        await remoteStart({
            chargePointId: chargePoint.id,
            connectorId,
            rfidTag,
        })
            .unwrap()
            .then(() => toast.notifySuccess(text('msgRemoteStartSuccess')))
            .catch(() => toast.notifyError(text(isAdminDomain ? 'msgRemoteStartAdminFailed' : 'msgRemoteStartFailed')))
    }

    const handleRemoteControl = async (connectorId: number, isNfcLockOn: boolean) => {
        setCurrentConnector(connectorId)
        const connector = chargePoint?.connectors?.find((connector) => connector.connectorId === connectorId)
        if (remoteStopIsAvailable(connector ?? null)) {
            await remoteStop({
                chargePointId: chargePoint.id,
                connectorId,
            })
                .unwrap()
                .then(() => toast.notifySuccess(text('msgRemoteStopSuccess')))
                .catch(() =>
                    toast.notifyError(text(isAdminDomain ? 'msgRemoteStopAdminFailed' : 'msgRemoteStopFailed'))
                )
        } else {
            if (isNfcLockOn) {
                setSelectRfidModalIsOpen(true)
            } else if (connector?.defaultNfcTagId) {
                handleRemoteStart(connectorId, connector?.defaultNfcTagId)
            } else {
                toast.notifyError(text('msgRemoteStartFailed'))
            }
        }
    }

    const handleOverrideSchedule = async (connectorId: number) => {
        setCurrentConnector(connectorId)

        await overrideSchedule({
            chargePointId: chargePoint.id,
            connectorId: connectorId,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgIgnoreScheduleSuccess'))
            })
            .catch((error) => {
                toast.notifyError(showErrorMessagesForIgnoreSchedule(error.data.error))
            })
    }
    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblAuraControlOcppWidget'}
        >
            {chargePoint?.connectors?.map((connector) => {
                if (!isUserHasAccessToConnector(connector, chargePoint?.ownerId, userId)) return null
                return (
                    <OcppAuraConnectorControl
                        connector={connector}
                        isReadOnly={isReadOnly}
                        isOnline={isOnline}
                        chargePoint={chargePoint}
                        key={connector.connectorId}
                        onRemoteControl={({ connectorId, isNfcLockOn }) =>
                            handleRemoteControl(connectorId, isNfcLockOn)
                        }
                        handleOverrideSchedule={(connectorId) => handleOverrideSchedule(connectorId)}
                    />
                )
            })}
            {isUserHasAccessToConnector(chargePoint?.connectors[0], chargePoint?.ownerId, userId) &&
                isUserHasAccessToConnector(chargePoint?.connectors[1], chargePoint.ownerId, userId) && (
                    <RemoteOcppDimmerControl chargePoint={chargePoint} isReadOnly={isReadOnly} />
                )}
            <SelectRFIDTagModal
                isOpen={selectRfidModalIsOpen}
                chargePointId={chargePoint.id}
                connectorId={currentConnector}
                onStartCharging={(rfidTag) => {
                    setSelectRfidModalIsOpen(false)
                    handleRemoteStart(currentConnector, rfidTag)
                }}
                onClose={() => setSelectRfidModalIsOpen(false)}
            />
        </RemoteControlWidget>
    )
}
